import React, { useEffect, useState } from "react";
import {
  Box,
  ThemeProvider,
  CssBaseline,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { avansTheme } from "./theme";
import Sidebar from "./components/Sidebar";
import RightPanel from "./components/RightPanel";
import ProgressBar from "./components/ProgressBar";
import Step1BasicInfo from "./steps/Step1BasicInfo";
import Step2CourseDiscovery from "./steps/Step2CourseDiscovery";
import Step3LoanDetails from "./steps/Step3LoanDetails";
import SuccessScreen from "./steps/SuccessScreen";
import { FormValues } from "./types";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import {
  setCurrentStep,
  setBasicDetails,
  setCourseDetails,
  setLoanDetails,
} from "../../services/redux/slices/newJourneyEligibilitycheckSlice";
import MainHeader from "./components/MainHeader";

const PROGRESS = {
  STEP1_DONE: 33,
  STEP2_DONE: 66,
  STEP3_ACTIVE: 90,
  COMPLETE: 100,
} as const;

const isStep1Complete = (basicDetails: any): boolean => {
  if (!basicDetails) return false;
  return !!(
    basicDetails.firstName?.trim() &&
    basicDetails.lastName?.trim() &&
    basicDetails.phone_number?.trim() &&
    basicDetails.email?.trim() &&
    basicDetails.pincode?.trim() &&
    basicDetails.terms_conditions &&
    basicDetails.privacy_policy &&
    basicDetails.isMobileVerified
  );
};


const isStep2Complete = (courseDetails: any): boolean => {
  if (!courseDetails) return false;
  const isUndecided = courseDetails.admissionStatus === "Undecided";
  const hasCore =
    !!courseDetails.courseCategory &&
    !!courseDetails.admissionStatus &&
    !!(typeof courseDetails.country === "object"
      ? courseDetails.country?.value
      : courseDetails.country) &&
    !!courseDetails.courseType;

  if (!hasCore) return false;
  if (isUndecided) return true;

  const getVal = (f: any) => (typeof f === "object" ? f?.value || f?.label : f);
  return !!(
    getVal(courseDetails.university) &&
    getVal(courseDetails.institute) &&
    getVal(courseDetails.courseName)
  );
};

const AppContent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();

  // ── Global state from Redux ───────────────────────────────────────────────────

  const currentStep: number = useSelector(
    (state: RootState) => state?.eligibilityCheck?.currentStep,
  );

  // All form data lives in the Redux slice — no local formData state needed.
  // initialValues for each step are read directly from the slice.
  const basicDetails = useSelector(
    (state: any) => state?.eligibilityCheck?.stepFields?.basicDetails,
  );
  const courseDetails = useSelector(
    (state: any) => state?.eligibilityCheck?.stepFields?.courseDetails,
  );
  const loanDetails = useSelector(
    (state: any) => state?.eligibilityCheck?.stepFields?.loanDetails,
  );
  const trackingId = useSelector(
    (state: RootState) => state?.eligibilityCheck?.trackingId,
  );

  const computeProgress = (): number => {
    if (currentStep === 4) return PROGRESS.COMPLETE;
    if (currentStep === 3) return PROGRESS.STEP3_ACTIVE;

    const step1Done = isStep1Complete(basicDetails);
    const step2Done = isStep2Complete(courseDetails);

    if (currentStep === 2) {
      return step2Done ? PROGRESS.STEP2_DONE : PROGRESS.STEP1_DONE;
    }

    return step1Done ? PROGRESS.STEP1_DONE : 0;
  };

  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [basicDetailsFields, setBasicDetailsFields] = useState<any[]>([]);
  const [courseDetailsFields, setCourseDetailsFields] = useState<any[]>([]);
  const [loanDetailsFields, setLoanDetailsFields] = useState<any[]>([]);

  const authProfile = useSelector(
    (state: RootState) => state?.auth?.userProfile,
  );

  const product = "SLI";

  // ── Step handlers ─────────────────────────────────────────────────────────────
  // Each handler saves step data to Redux then advances the step counter.

  const handleStepNext = (stepData: Partial<FormValues>) => {
    // Persist step data into the appropriate slice field
    if (currentStep === 1) {
      dispatch(setBasicDetails(stepData as any));
    } else if (currentStep === 2) {
      dispatch(setCourseDetails(stepData as any));
    }

    setCompletedSteps((prev) =>
      prev.includes(currentStep) ? prev : [...prev, currentStep],
    );
    dispatch(setCurrentStep(currentStep + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    dispatch(setCurrentStep(currentStep - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFinalSubmit = (stepData: Partial<FormValues>) => {
    dispatch(setLoanDetails(stepData as any));
    setCompletedSteps((prev) =>
      prev.includes(currentStep) ? prev : [...prev, currentStep],
    );
    dispatch(setCurrentStep(4));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  // ── Fetch form field config ───────────────────────────────────────────────────

  const fetchFormJson = async () => {
    try {
      const payload = {
        productCode: product || "SLI",
        id: authProfile?.id,
        trackingId: trackingId,
      };

      const res = await eligibilityCheckServices.getForm(payload);
      if (res.status === 200) {
        const response: any = res.data.data;

        const formJson: any = response.horizontalSteps.filter(
          (elem: any) => elem?.stepKey == "eligibility_check",
        );
        setBasicDetailsFields(formJson?.[0]?.verticalSteps?.[0]?.fields || []);
        setCourseDetailsFields(formJson?.[0]?.verticalSteps?.[1]?.fields || []);
        setLoanDetailsFields(formJson?.[0]?.verticalSteps?.[2]?.fields || []);
      } else {
        throw new Error("Failed to fetch form JSON");
      }
    } catch (error: any) {
      console.error("Error fetching form JSON:", error);
    }
  };

  useEffect(() => {
    fetchFormJson();
  }, [currentStep]);

  // ── Step renderer — initialValues come from Redux, not local state ────────────

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            fields={basicDetailsFields}
            initialValues={basicDetails}
            onNext={handleStepNext}
            currentStep={currentStep}
            progress={computeProgress()}
            completedSteps={completedSteps}
          />
        );
      case 2:
        return (
          <Step2CourseDiscovery
            initialValues={courseDetails}
            fields={courseDetailsFields}
            onNext={handleStepNext}
            onBack={handleBack}
            currentStep={currentStep}
            progress={computeProgress()}
            completedSteps={completedSteps}
          />
        );
      case 3:
        return (
          <Step3LoanDetails
            initialValues={loanDetails}
            fields={loanDetailsFields}
            onSubmit={handleFinalSubmit}
            onBack={handleBack}
            currentStep={currentStep}
            progress={computeProgress()}
            completedSteps={completedSteps}
          />
        );
      case 4:
        return (
          <SuccessScreen
            formData={{ ...basicDetails, ...courseDetails, ...loanDetails }}
            currentStep={currentStep}
            progress={computeProgress()}
            completedSteps={completedSteps}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: isMobile ? "" : "#EBF5F6",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MainHeader />

      {/* ── Main Layout ── */}
      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Desktop Left Sidebar — sticky, white bg */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              bgcolor: "#FBFBFB",
              borderRight: "1px solid",
              borderColor: "grey.200",
              px: 2,
              py: 3,
              display: "flex",
              flexDirection: "column",
              position: "sticky",
              top: 64,
              overflowY: "auto",
            }}
          >
            <Sidebar
              currentStep={currentStep === 4 ? 3 : currentStep}
              completedSteps={completedSteps}
            />
          </Box>
        )}

        {/* Centre column — Progress bar on top, scrollable content below */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflowY: "auto",
          }}
        >
          {currentStep <= 4 && !isMobile && <ProgressBar progress={computeProgress()} />}

          <Box
            sx={{
              flex: 1,
              p: { md: 4 },
              maxWidth: currentStep === 4 ? "100%" : "unset",
            }}
          >
            <Box sx={{ mx: "auto" }}>{renderStep()}</Box>
          </Box>
        </Box>
      </Box>

      {!isMobile && <Box
        component="footer"
        sx={{
          bgcolor: "transparent",
          pr: { xs: 2, md: 5 },
          pl: { xs: 2, md: 34 },
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 3 }}>
          {[
            {
              label: "Privacy Policy",
              path: "https://www.avanse.com/privacy-policy",
            },
            {
              label: "Terms of Service",
              path: "https://www.avanse.com/terms-conditions",
            },
          ].map((val: { label: string; path: string }) => (
            <Typography
              key={val.label}
              component="a"
              href={val.path}
              target="_blank"
              sx={{
                fontSize: "0.78rem",
                color: "text.secondary",
                textDecoration: "none",
                "&:hover": { color: "primary.main" },
              }}
            >
              {val.label}
            </Typography>
          ))}
        </Box>
        <Typography sx={{ fontSize: "0.75rem", color: "#3D494899" }}>
          © 2026 Avanse. All rights reserved.
        </Typography>
      </Box>}
    </Box>
  );
};

const PreLoginJourney: React.FC = () => {
  return (
    <ThemeProvider theme={avansTheme}>
      <CssBaseline />

      <AppContent />
    </ThemeProvider>
  );
};

export default PreLoginJourney;